import React, { useState, useEffect, useCallback } from 'react';
import {
    Lightbulb, Bug, Star, MessageSquare, RefreshCw,
    Loader2, AlertTriangle, ChevronDown, ChevronUp,
    AlertCircle, ArrowRight, Zap, Flame, CheckCircle,
    Clock, ExternalLink,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import MetricCard from './MetricCard';
import { Section } from './OverviewTabV2';

/**
 * FeedbackInsightsTab — "What are users frustrated by, and what needs action next?"
 *
 * Layout:
 *   1. Action Priority Queue   — prioritized cards with explicit next steps
 *   2. Issue Clusters          — what users are hitting
 *   3. Suggestion Clusters     — what users want
 *   4. Recent Unresolved       — raw items (collapsed by default)
 *   5. Feedback KPIs           — context metrics (bottom)
 */

// ─── Build actionable cards from raw data ────────────────────────────────────

/**
 * Synthesize the Action Priority Queue from API data.
 * Deterministic rules produce grounded cards; AI insights produce generated cards.
 */
const buildActionCards = ({ openBugs, issueClusters, suggestionClusters, insightCards, recentUnresolved }) => {
    const cards = [];

    // --- Deterministic: open bugs ---
    if (openBugs > 0) {
        cards.push({
            id: 'open_bugs',
            priority: 'high',
            source_type: 'deterministic',
            title: `${openBugs} Open Bug${openBugs !== 1 ? 's' : ''} Awaiting Resolution`,
            body: `There ${openBugs === 1 ? 'is' : 'are'} ${openBugs} unresolved bug report${openBugs !== 1 ? 's' : ''} in the feedback queue. These represent confirmed friction points teachers have explicitly reported.`,
            action: 'feedback-triage',
            action_label: 'Review in Feedback Triage →',
            icon: Bug,
            color: '#ef4444',
        });
    }

    // --- Deterministic: top issue cluster ---
    const topCluster = issueClusters?.[0];
    if (topCluster && (topCluster.item_count || 0) >= 2) {
        cards.push({
            id: 'top_cluster',
            priority: 'high',
            source_type: 'deterministic',
            title: `Recurring Issue: "${topCluster.theme || topCluster.feature_area || 'Unknown area'}"`,
            body: `${topCluster.item_count} similar feedback items cluster around this issue. This is a pattern, not a one-off — it warrants investigation.`,
            action: 'scroll-issue-clusters',
            action_label: 'See Issue Clusters below ↓',
            icon: AlertCircle,
            color: '#f97316',
        });
    }

    // --- Deterministic: recent unresolved ---
    if (recentUnresolved.length >= 3) {
        cards.push({
            id: 'unresolved_backlog',
            priority: 'medium',
            source_type: 'deterministic',
            title: `${recentUnresolved.length} Unresolved Items Building Up`,
            body: `Recent feedback is accumulating unresolved. The oldest unresolved item is getting stale. Triage now to prevent trust erosion with teachers.`,
            action: 'scroll-unresolved',
            action_label: 'Jump to Recent Unresolved ↓',
            icon: Clock,
            color: '#d97706',
        });
    }

    // --- Deterministic: top suggestion cluster ---
    const topSuggestion = suggestionClusters?.[0];
    if (topSuggestion && (topSuggestion.item_count || 0) >= 2) {
        cards.push({
            id: 'top_suggestion',
            priority: 'medium',
            source_type: 'deterministic',
            title: `High-Demand Request: "${topSuggestion.theme || topSuggestion.feature_area || 'Unknown area'}"`,
            body: `${topSuggestion.item_count} teachers have independently requested something in this area. This is a clear product opportunity signal.`,
            action: 'scroll-suggestion-clusters',
            action_label: 'See Suggestion Clusters below ↓',
            icon: Lightbulb,
            color: '#8b5cf6',
        });
    }

    // --- AI-generated insights from snapshot ---
    const aiCards = (insightCards || []).slice(0, 3);
    for (const card of aiCards) {
        if (!card.title && !card.headline) continue;
        cards.push({
            id: `ai_${card.card_type || Math.random()}`,
            priority: card.priority || 'low',
            source_type: 'generated',
            title: card.title || card.headline,
            body: card.body || card.summary || card.description || '',
            metric: card.metric || null,
            icon: Zap,
            color: '#6366f1',
        });
    }

    // Sort: high → medium → low, then deterministic before generated
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    const sourceOrder = { deterministic: 0, generated: 1 };
    cards.sort((a, b) => {
        const pDiff = (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2);
        if (pDiff !== 0) return pDiff;
        return (sourceOrder[a.source_type] ?? 1) - (sourceOrder[b.source_type] ?? 1);
    });

    return cards;
};


// ─── Main component ───────────────────────────────────────────────────────────

const FeedbackInsightsTab = ({ filters = {}, onTabChange }) => {
    const [themes, setThemes] = useState(null);
    const [issues, setIssues] = useState(null);
    const [suggestions, setSuggestions] = useState(null);
    const [insights, setInsights] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');

    // Scroll refs for action cards
    const issueRef = React.useRef(null);
    const suggestionRef = React.useRef(null);
    const unresolvedRef = React.useRef(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                days: filters.days || 30,
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };

            const [thRes, isRes, sgRes, inRes] = await Promise.allSettled([
                adminAnalytics.getFeedbackThemes(params),
                adminAnalytics.getIssueClusters(params),
                adminAnalytics.getSuggestionClusters(params),
                adminAnalytics.getInsightsLatest(params),
            ]);

            if (thRes.status === 'fulfilled') setThemes(thRes.value.data);
            if (isRes.status === 'fulfilled') setIssues(isRes.value.data);
            if (sgRes.status === 'fulfilled') setSuggestions(sgRes.value.data);
            if (inRes.status === 'fulfilled') setInsights(inRes.value.data?.snapshot || inRes.value.data);
        } catch (err) {
            setError(err.message || 'Failed to load feedback data.');
        } finally {
            setLoading(false);
        }
    }, [filters.days, filters.district_id, filters.campus_id]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleRefreshInsights = async () => {
        setRefreshing(true);
        try {
            const res = await adminAnalytics.refreshInsights();
            if (res.data?.snapshot) setInsights(res.data.snapshot);
            const [isRes, sgRes] = await Promise.allSettled([
                adminAnalytics.getIssueClusters(),
                adminAnalytics.getSuggestionClusters(),
            ]);
            if (isRes.status === 'fulfilled') setIssues(isRes.value.data);
            if (sgRes.status === 'fulfilled') setSuggestions(sgRes.value.data);
        } catch { /* silent */ }
        finally { setRefreshing(false); }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading feedback & insights…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
                <button onClick={loadData} style={{ fontSize: 13, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>
                    Retry
                </button>
            </div>
        );
    }

    const catFeedback = themes?.categorized_feedback || {};
    const byCategory = catFeedback.by_category || [];
    const recentUnresolved = catFeedback.recent_unresolved || [];

    const openBugs = byCategory.find(c => c.category === 'bug')?.count || 0;
    const openFeatureRequests = byCategory.find(c => c.category === 'feature_request')?.count || 0;

    const issueClusters = issues?.clusters || [];
    const suggestionClusters = suggestions?.clusters || [];
    const insightCards = insights?.cards || [];

    const actionCards = buildActionCards({
        openBugs,
        openImprovements: byCategory.find(c => c.category === 'improvement')?.count || 0,
        issueClusters,
        suggestionClusters,
        insightCards,
        recentUnresolved,
    });

    // Handle action navigation
    const handleActionClick = (action) => {
        if (action === 'feedback-triage' && onTabChange) {
            onTabChange('feedback-insights'); // stays here, user already on this tab — but we could navigate to AdminFeedback
            // For now just scroll to top; Feedback Triage is separate in AdminWorkspace
        }
        if (action === 'scroll-issue-clusters') {
            issueRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        if (action === 'scroll-suggestion-clusters') {
            suggestionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        if (action === 'scroll-unresolved') {
            unresolvedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const snapshotAge = insights?.generated_at
        ? `Insight snapshot: ${new Date(insights.generated_at).toLocaleString()}`
        : 'No insight snapshot yet';

    return (
        <div>
            {/* ── Header ─────────────────────────────────────────────── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>
                        Signals & Insights
                    </h2>
                    <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                        What are users frustrated by, and what needs action next?
                    </p>
                </div>
                <button
                    onClick={handleRefreshInsights}
                    disabled={refreshing}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 8,
                        background: refreshing ? '#f8fafc' : '#eef2ff',
                        border: '1px solid #c7d2fe', color: '#4f46e5',
                        fontSize: 12, fontWeight: 600, cursor: refreshing ? 'default' : 'pointer',
                    }}
                >
                    <RefreshCw size={13} style={refreshing ? { animation: 'spin 1s linear infinite' } : {}} />
                    {refreshing ? 'Refreshing…' : 'Refresh Insights'}
                </button>
            </div>

            {/* ── 1. ACTION PRIORITY QUEUE ───────────────────────────── */}
            <ActionPriorityQueue
                cards={actionCards}
                onAction={handleActionClick}
                snapshotAge={snapshotAge}
                hasData={openBugs > 0 || issueClusters.length > 0 || insightCards.length > 0}
            />

            {/* ── 2. Issue Clusters ──────────────────────────────────── */}
            <div ref={issueRef}>
                <ClusterSection
                    title="Issue Clusters"
                    subtitle="Recurring problems grouped by theme"
                    icon={Bug}
                    color="#ef4444"
                    clusters={issueClusters}
                    trustLabel="DETERMINISTIC"
                    emptyText="No issue clusters found. Click Refresh Insights to generate."
                />
            </div>

            {/* ── 3. Suggestion Clusters ─────────────────────────────── */}
            <div ref={suggestionRef}>
                <ClusterSection
                    title="Suggestion Clusters"
                    subtitle="Teacher requests grouped by theme"
                    icon={Lightbulb}
                    color="#8b5cf6"
                    clusters={suggestionClusters}
                    trustLabel="DETERMINISTIC"
                    emptyText="No suggestion clusters found. Click Refresh Insights to generate."
                />
            </div>

            {/* ── 4. Recent Unresolved ───────────────────────────────── */}
            {recentUnresolved.length > 0 && (
                <div ref={unresolvedRef} style={{ marginBottom: 28 }}>
                    <SectionHead title="Recent Unresolved Feedback" icon={AlertCircle} color="#f97316"
                        badge="LIVE · RAW" badgeColor="#f97316"
                        subtitle={`${recentUnresolved.length} items awaiting triage`}
                    />
                    <div style={{
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
                        overflow: 'hidden',
                    }}>
                        {recentUnresolved.slice(0, 8).map((fb, i) => (
                            <div key={fb.id || i} style={{
                                padding: '12px 16px',
                                borderBottom: i < Math.min(recentUnresolved.length, 8) - 1 ? '1px solid #f1f5f9' : 'none',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                    <span style={{
                                        fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                                        background: fb.category === 'bug' ? '#fef2f2' : '#fffbeb',
                                        color: fb.category === 'bug' ? '#ef4444' : '#d97706',
                                        letterSpacing: '0.04em',
                                    }}>
                                        {(fb.category || 'other').toUpperCase()}
                                    </span>
                                    {fb.feature_area && (
                                        <span style={{ fontSize: 10, color: '#94a3b8' }}>{fb.feature_area}</span>
                                    )}
                                    {fb.created_at && (
                                        <span style={{ fontSize: 10, color: '#d1d5db', marginLeft: 'auto' }}>
                                            {new Date(fb.created_at).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.4 }}>
                                    {fb.body || fb.title || fb.description || '—'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── 5. Feedback KPIs (context, not hero) ──────────────── */}
            <Section title="Feedback Summary" icon={MessageSquare} color="#f59e0b">
                <MetricCard label="Open Bugs" value={openBugs} icon={Bug} accent="#ef4444" />
                <MetricCard label="Open Improvements" value={byCategory.find(c => c.category === 'improvement')?.count || 0} icon={Lightbulb} accent="#f59e0b" />
                <MetricCard label="Feature Requests" value={openFeatureRequests} icon={Star} accent="#8b5cf6" />
                <MetricCard
                    label="Insight Snapshot"
                    value={insights?.generated_at ? 'Available' : 'None'}
                    icon={Zap} accent="#3b82f6"
                    sub={snapshotAge}
                />
            </Section>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};


// ─── Action Priority Queue ───────────────────────────────────────────────────

const PRIORITY_CONFIG = {
    high:   { label: 'HIGH',    color: '#ef4444', bg: '#fef2f2', border: '#fecaca', barColor: '#ef4444' },
    medium: { label: 'MED',     color: '#d97706', bg: '#fffbeb', border: '#fde68a', barColor: '#f59e0b' },
    low:    { label: 'LOW',     color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', barColor: '#9ca3af' },
};

const SOURCE_TRUST = {
    deterministic: { label: 'DETERMINISTIC', color: '#3b82f6', bg: '#eff6ff', border: '#93c5fd' },
    generated:     { label: 'AI-GENERATED',  color: '#8b5cf6', bg: '#f5f3ff', border: '#c4b5fd' },
    live:          { label: 'LIVE',          color: '#10b981', bg: '#ecfdf5', border: '#6ee7b7' },
    partial:       { label: 'PARTIAL DATA',  color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
};

const ActionPriorityQueue = ({ cards, onAction, snapshotAge }) => {
    const highCards = cards.filter(c => c.priority === 'high');
    const medCards = cards.filter(c => c.priority === 'medium');
    const lowCards = cards.filter(c => c.priority === 'low');

    const allEmpty = cards.length === 0;

    return (
        <div style={{ marginBottom: 32 }}>
            <SectionHead
                title="Action Priority Queue"
                icon={Flame}
                color="#ef4444"
                badge="PRIORITIZED"
                badgeColor="#6366f1"
                subtitle="Ranked by impact — highest-friction items surfaced first"
                right={
                    <span style={{ fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                        {snapshotAge}
                    </span>
                }
            />

            {allEmpty && (
                <div style={{
                    background: '#f0fdf4', border: '1px solid #bbf7d0',
                    borderRadius: 12, padding: '24px', textAlign: 'center',
                }}>
                    <CheckCircle size={24} style={{ color: '#10b981', margin: '0 auto 8px', display: 'block' }} />
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#065f46', margin: 0 }}>
                        No action items right now
                    </p>
                    <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                        Click "Refresh Insights" to generate analysis from current feedback and analytics data.
                    </p>
                </div>
            )}

            {/* HIGH priority */}
            {highCards.length > 0 && (
                <PriorityGroup
                    label="High Priority"
                    labelColor="#ef4444"
                    cards={highCards}
                    onAction={onAction}
                />
            )}

            {/* MEDIUM priority */}
            {medCards.length > 0 && (
                <PriorityGroup
                    label="Medium Priority"
                    labelColor="#d97706"
                    cards={medCards}
                    onAction={onAction}
                />
            )}

            {/* LOW — collapsed by default */}
            {lowCards.length > 0 && (
                <CollapsiblePriorityGroup
                    label={`${lowCards.length} low-priority item${lowCards.length !== 1 ? 's' : ''}`}
                    cards={lowCards}
                    onAction={onAction}
                />
            )}
        </div>
    );
};

const PriorityGroup = ({ label, labelColor, cards, onAction }) => (
    <div style={{ marginBottom: 12 }}>
        <div style={{
            fontSize: 10, fontWeight: 700, color: labelColor,
            letterSpacing: '0.07em', textTransform: 'uppercase',
            marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
        }}>
            <span style={{
                display: 'inline-block', width: 6, height: 6,
                borderRadius: '50%', background: labelColor,
            }} />
            {label}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {cards.map(card => (
                <ActionCard key={card.id} card={card} onAction={onAction} />
            ))}
        </div>
    </div>
);

const CollapsiblePriorityGroup = ({ label, cards, onAction }) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <div style={{ marginBottom: 12 }}>
            <button
                onClick={() => setExpanded(v => !v)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontSize: 11, color: '#94a3b8', background: 'none', border: 'none',
                    cursor: 'pointer', padding: '4px 0', fontWeight: 500,
                }}
            >
                {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                {expanded ? 'Hide' : 'Show'} {label}
            </button>
            {expanded && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                    {cards.map(card => (
                        <ActionCard key={card.id} card={card} onAction={onAction} />
                    ))}
                </div>
            )}
        </div>
    );
};

const ActionCard = ({ card, onAction }) => {
    const Icon = card.icon || AlertCircle;
    const priCfg = PRIORITY_CONFIG[card.priority] || PRIORITY_CONFIG.low;
    const srcCfg = SOURCE_TRUST[card.source_type] || SOURCE_TRUST.generated;

    return (
        <div style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderLeft: `4px solid ${priCfg.barColor}`,
            borderRadius: '0 12px 12px 0',
            padding: '14px 16px',
            display: 'flex', flexDirection: 'column', gap: 10,
        }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{
                    width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                    background: `${card.color || '#6366f1'}12`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Icon size={14} style={{ color: card.color || '#6366f1' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
                            {card.title}
                        </span>
                        {/* Priority badge */}
                        <span style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                            padding: '2px 6px', borderRadius: 4,
                            background: priCfg.bg, color: priCfg.color,
                            border: `1px solid ${priCfg.border}`,
                        }}>
                            {priCfg.label}
                        </span>
                        {/* Trust source badge — explicit text */}
                        <span style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                            padding: '2px 6px', borderRadius: 4,
                            background: srcCfg.bg, color: srcCfg.color,
                            border: `1px solid ${srcCfg.border}`,
                        }}>
                            {srcCfg.label}
                        </span>
                    </div>
                    <p style={{ fontSize: 12, color: '#6b7280', margin: 0, lineHeight: 1.55 }}>
                        {card.body}
                    </p>
                    {card.metric && (
                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, fontFamily: 'monospace' }}>
                            metric: {card.metric}
                        </div>
                    )}
                </div>
            </div>

            {/* Next-step affordance */}
            {card.action && (
                <button
                    onClick={() => onAction && onAction(card.action, card)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 8, alignSelf: 'flex-start',
                        background: `${card.color || '#6366f1'}0d`,
                        border: `1px solid ${card.color || '#6366f1'}30`,
                        color: card.color || '#6366f1',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    }}
                    onMouseOver={e => { e.currentTarget.style.background = `${card.color || '#6366f1'}18`; }}
                    onMouseOut={e => { e.currentTarget.style.background = `${card.color || '#6366f1'}0d`; }}
                >
                    <ArrowRight size={12} />
                    {card.action_label || 'Review →'}
                </button>
            )}
        </div>
    );
};


// ─── Cluster Section ─────────────────────────────────────────────────────────

const ClusterSection = ({ title, subtitle, icon: Icon, color, clusters, trustLabel, emptyText }) => {
    const [expandedIdx, setExpandedIdx] = useState(null);

    return (
        <div style={{ marginBottom: 28 }}>
            <SectionHead
                title={title}
                subtitle={subtitle}
                icon={Icon}
                color={color}
                badge={trustLabel}
                badgeColor={color}
                right={
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                        {clusters.length} cluster{clusters.length !== 1 ? 's' : ''}
                    </span>
                }
            />

            {clusters.length === 0 ? (
                <div style={{
                    background: '#f9fafb', border: '1px solid #e5e7eb',
                    borderRadius: 12, padding: '24px', textAlign: 'center',
                }}>
                    <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>{emptyText}</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {clusters.slice(0, 8).map((cluster, idx) => (
                        <div key={idx} style={{
                            background: '#fff', border: '1px solid #e5e7eb',
                            borderRadius: 12, overflow: 'hidden',
                        }}>
                            <div
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '12px 16px', cursor: 'pointer',
                                }}
                                onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                                    <span style={{
                                        width: 8, height: 8, borderRadius: '50%',
                                        background: color, flexShrink: 0,
                                    }} />
                                    <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                                        {cluster.theme || cluster.feature_area || `Cluster ${idx + 1}`}
                                    </span>
                                    <span style={{
                                        fontSize: 10, padding: '2px 7px', borderRadius: 10,
                                        background: `${color}12`, color, fontWeight: 600,
                                    }}>
                                        {cluster.item_count || 0} items
                                    </span>
                                </div>
                                {expandedIdx === idx
                                    ? <ChevronUp size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
                                    : <ChevronDown size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
                                }
                            </div>
                            {expandedIdx === idx && cluster.items && (
                                <div style={{ padding: '0 16px 12px', borderTop: '1px solid #f1f5f9' }}>
                                    {cluster.items.slice(0, 5).map((item, j) => (
                                        <div key={j} style={{
                                            fontSize: 12, color: '#6b7280', padding: '7px 0',
                                            borderBottom: j < Math.min(cluster.items.length, 5) - 1 ? '1px solid #f8fafc' : 'none',
                                            lineHeight: 1.45,
                                        }}>
                                            {item.body || item.title || item.description || JSON.stringify(item)}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};


// ─── SectionHead ─────────────────────────────────────────────────────────────

const SectionHead = ({ title, subtitle, icon: Icon, color, badge, badgeColor, right }) => {
    const bcfg = SOURCE_TRUST[badge?.toLowerCase()] || {
        label: badge,
        color: badgeColor || '#6366f1',
        bg: `${badgeColor || '#6366f1'}12`,
        border: `${badgeColor || '#6366f1'}30`,
    };
    return (
        <div style={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            marginBottom: 12, paddingBottom: 8,
            borderBottom: `2px solid ${color}20`,
            gap: 10,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {Icon && <Icon size={15} style={{ color }} />}
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: 0 }}>{title}</h3>
                {badge && (
                    <span style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: '0.07em',
                        padding: '2px 7px', borderRadius: 4,
                        background: bcfg.bg, color: bcfg.color, border: `1px solid ${bcfg.border}`,
                    }}>
                        {bcfg.label || badge}
                    </span>
                )}
                {subtitle && (
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{subtitle}</span>
                )}
            </div>
            {right && <div>{right}</div>}
        </div>
    );
};


export default FeedbackInsightsTab;
